import type { ReactNode } from "react";
import { factChecks, type Verdict } from "@/mocks/homeData";
import { Shell } from "./ui";

const verdictStyle: Record<Verdict, { icon: string; cls: string; label: string }> = {
  真: { icon: "ri-checkbox-circle-fill", cls: "text-background-50", label: "已证实" },
  假: { icon: "ri-close-circle-fill", cls: "text-accent-300", label: "系谣言" },
  存疑: { icon: "ri-question-line", cls: "text-background-100/70", label: "待核实" },
};

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-background-100/20 bg-background-100/10 px-2 py-0.5 text-[11px] font-semibold text-accent-300">
      <i className="ri-verified-badge-line" />
      黑料王判定
    </span>
  );
}

function Badge({ v }: { v: Verdict }) {
  const s = verdictStyle[v];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-bold ${s.cls}`}>
      <i className={s.icon} />
      {v}
      <span className="text-[11px] font-medium opacity-70">· {s.label}</span>
    </span>
  );
}

export default function FactCheckSection() {
  return (
    <section className="bg-foreground-950 text-background-100">
      <Shell>
        <div className="py-10 md:py-14">
          {/* 暗色区标题 */}
          <div id="fact" className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-accent-400" />
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-accent-400">Fact Check · 真假实验室</span>
              </div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight text-background-50 md:text-[28px]">
                辟谣求真 · 一句话辨真假
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-100/70">
                把传得最凶的说法摆上台面，用可复核的证据给出「真 / 假 / 存疑」三种结论，并随事件后续持续修正。
              </p>
            </div>
            <a href="#report" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-accent-500/50 px-3 py-1.5 text-xs font-semibold text-accent-300 transition hover:bg-accent-500 hover:text-foreground-950">
              <i className="ri-send-plane-line" />投稿求证
            </a>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {factChecks.map((f) => (
              <article
                key={f.id}
                className="rounded-xl border border-background-100/15 bg-background-100/5 p-4 transition hover:border-accent-500/40 md:p-5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Pill><i className="ri-verified-badge-line" />黑料王判定</Pill>
                  <span className="text-[11px] text-background-100/50">{f.time}</span>
                </div>
                <h3 className="mt-2 flex flex-wrap items-start justify-between gap-3 font-heading text-base font-bold leading-snug text-background-50">
                  <span>{f.claim}</span>
                  <Badge v={f.verdict} />
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/75">
                  <span className="mr-1.5 inline-flex items-center gap-1 text-xs text-accent-500"><i className="ri-information-line" />核验说明</span>
                  {f.detail}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-background-100/15 bg-background-100/5 p-4 sm:flex-row sm:items-center md:p-5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-100/70">
              <span className="inline-flex items-center gap-1.5"><i className="ri-video-line text-accent-500" />比对原视频回放</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-file-search-line text-accent-500" />核验第三方存档</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-focus-2-line text-accent-500" />逐帧与时间戳分析</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-shield-check-line text-accent-500" />证据入库存证</span>
            </div>
            <span className="text-xs text-background-100/50">判定随新证据持续修正 · 结论仅供参考</span>
          </div>
        </div>
      </Shell>
    </section>
  );
}